import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../context/AppContext";
import { BasketCorrection } from "../components/BasketCorrection";

// Purchases (docs/tasks/31): every purchase a cabinet made, with what its
// review decided and why, and - where the cabinet sent them - anonymised crops
// of the hands and products (basket only, face-screened on the cabinet).
// For settling a dispute with a customer: find the purchase by time and the
// merchant's basket id, read the reasons, look at the crops.

const OUTCOME = {
  charged: { label: "Charged", cls: "bg-emerald-100 text-emerald-800" },
  uncertain: { label: "Uncertain", cls: "bg-amber-100 text-amber-800" },
  empty: { label: "Empty cart", cls: "bg-rose-100 text-rose-800" },
};

// What each flag means to the person settling a dispute (handbook ch. 16).
const FLAG = {
  lid_overopened: "The camera could not see the basket for a moment (lid lifted too far, or the camera covered)",
  weight_disagrees: "A weight tray and a camera disagreed; the tray's count was charged",
  cloud_check_adjusted: "The cloud check corrected the cart before payment",
  cloud_disagrees_after_payment: "The cloud check would have changed the cart, but the basket was already in payment",
  cloud_check_adjust_failed: "The cloud check found a change but could not apply it",
  corrected_by_operator: "An operator corrected the basket by hand (see the basket below)",
  outstanding_after_correction: "After an operator's correction the customer owes more than was charged",
  touched: "A product was picked up and its direction was unclear",
  tie: "Two look-alike products scored alike",
  cameras_disagree: "The cameras named different products; nothing was charged for them",
  direction_dispute: "Cameras disagreed on direction; charged as taken",
  witness_only: "Only the bottom camera saw the take",
  review_error: "The review failed",
  no_review: "No opening was reviewed",
  motion_without_verdict: "There was movement, but no product was recognised",
  returned: "A product was put back; nothing was charged",
  nothing_seen: "The cameras saw no product at all - see what they saw (lid opened too far?)",
};

function flagText(f) {
  if (f.startsWith("review_skipped:")) return `The review was skipped (${f.split(":")[1]})`;
  return FLAG[f] || f;
}

// Records written before release -80 carry the review's details in Russian.
const LEGACY = [
  [/треков (\d+) \(движ\. (\d+)\)/g, "tracks $1 (moving $2)"],
  [/ ушёл с места/g, " left its place"], [/ остался на месте/g, " still in place"],
  [/ цвет /g, " colour "], [/, на полке (\d+) набл\. в (\d+) местах: /g, ", on shelf $1 sightings in $2 places: "],
  [/(\d+)к t/g, "$1f t"], [/ визит/g, " visit"], [/ рост/g, " grow"], [/ край/g, " edge"],
  [/ свид\. ref/g, " witness ref"], [/ конец: /g, " end: "], [/ конец/g, " end"], [/ кадров/g, " frames"],
  [/появилось окон/g, "windows appeared"], [/опустело мест/g, "places emptied"],
  [/окон на месте/g, "windows in place"], [/последние кадры/g, "last frames"], [/кадров /g, "frames "],
  [/нет кадров/g, "no frames"],
];
function english(text) {
  return LEGACY.reduce((t, [re, to]) => t.replace(re, to), String(text || ""));
}

// Verdicts name products by the first 8 characters of their uuid.
function nameOf(names, id) {
  if (!id) return "—";
  return names[id] || names[String(id).slice(0, 8)] || id;
}

function fmt(ts) {
  return ts ? new Date(ts * 1000).toLocaleString() : "—";
}

function toEpoch(local) {
  return local ? String(Math.floor(new Date(local).getTime() / 1000)) : "";
}

function CropPlayer({ crops, names }) {
  // The crops of one camera in time order, played like a short clip.
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(false);
  const timer = useRef(null);
  useEffect(() => {
    if (!playing) return undefined;
    timer.current = setInterval(() => setI((k) => (k + 1) % crops.length), 350);
    return () => clearInterval(timer.current);
  }, [playing, crops.length]);
  const c = crops[Math.min(i, crops.length - 1)];
  if (!c) return null;
  return (
    <div className="space-y-2">
      <div className="flex h-64 items-center justify-center rounded bg-black">
        {c.url ? <img src={c.url} alt={c.name} className="max-h-64 max-w-full object-contain" /> : <span className="text-xs text-slate-400">link expired - reload</span>}
      </div>
      <div className="flex items-center gap-3 text-xs">
        <button className="btn btn-secondary" onClick={() => setPlaying((p) => !p)}>{playing ? "Pause" : "Play"}</button>
        <input type="range" min={0} max={crops.length - 1} value={i} onChange={(e) => { setPlaying(false); setI(Number(e.target.value)); }} className="flex-1" />
        <span className="font-mono">t+{c.t?.toFixed(2)} s · {nameOf(names, c.product)} · {c.kind}</span>
      </div>
    </div>
  );
}

export function PurchaseDetail({ sessionId, names: catalogue, deviceName = (id) => id, link = true }) {
  const api = useApi();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    setP(null);
    setErr(null);
    api.getPurchase(sessionId).then(setP).catch((e) => setErr(String(e.message || e)));
  }, [api, sessionId]);
  const scenes = useMemo(() => (p?.evidence?.crops || []).filter((c) => c.kind === "scene").sort((a, b) => a.t - b.t), [p]);
  const byCamera = useMemo(() => {
    const out = {};
    for (const c of (p?.evidence?.crops || []).filter((x) => x.kind !== "scene")) (out[c.camera] = out[c.camera] || []).push(c);
    for (const k of Object.keys(out)) out[k].sort((a, b) => a.t - b.t);
    return out;
  }, [p]);
  if (err) return <p className="text-sm text-rose-700">{err}</p>;
  if (!p) return <p className="text-sm text-slate-500">Loading…</p>;
  const ev = p.evidence || {};
  // The session's own cart carries the names the cabinet charged under.
  const names = { ...catalogue };
  for (const c of p.session?.cart || []) {
    if (c.product_uuid && c.product_name) { names[c.product_uuid] = c.product_name; names[c.product_uuid.slice(0, 8)] = c.product_name; }
  }
  return (
    <div className="grid gap-6 p-2 lg:grid-cols-2">
      <div className="space-y-4">
        <div>
          <div className="label">Purchase{link && p.device_id ? <> · <Link className="normal-case text-sky-700 hover:underline" to={`/cabinets/${p.device_id}/openings/${p.session_id}`}>open its page (timeline, cloud checks)</Link></> : null}</div>
          <dl className="grid grid-cols-[8rem_1fr] gap-y-1 text-sm">
            <dt className="text-slate-500">Basket id</dt><dd className="font-mono">{p.external_session_id || "—"}</dd>
            <dt className="text-slate-500">Session</dt><dd className="font-mono text-xs">{p.session_id}</dd>
            <dt className="text-slate-500">Cabinet</dt><dd className="text-sm">{deviceName(p.device_id)} <span className="font-mono text-xs text-slate-500">{p.device_id}</span></dd>
            <dt className="text-slate-500">Door</dt><dd>{fmt(p.started_at)} → {fmt(p.closed_at)}</dd>
            <dt className="text-slate-500">Software</dt><dd className="text-xs">{p.release || "—"} · model {p.model || "—"}</dd>
          </dl>
        </div>
        <div>
          <div className="label">Cart the cabinet settled</div>
          {p.cart.length === 0 ? <p className="text-sm">Nothing charged.</p> : (
            <ul className="text-sm">{p.cart.map((c, i) => <li key={i}>{c.quantity}× {nameOf(names, c.product_uuid)} <span className="font-mono text-xs text-slate-500">{c.ean_code}</span></li>)}</ul>
          )}
        </div>
        <BasketCorrection sessionId={p.session_id} />
        {p.flags.length > 0 && (
          <div>
            <div className="label">Why the cabinet was unsure</div>
            <ul className="list-disc pl-5 text-sm">{p.flags.map((f) => <li key={f}>{flagText(f)}</li>)}</ul>
          </div>
        )}
        <div>
          <div className="label">What the review saw</div>
          {p.openings.map((o, i) => (
            <div key={i} className="mb-2 text-xs">
              <div className="font-semibold">Opening {i + 1}{o.error ? ` - error: ${o.error}` : ""}{o.skipped ? ` - skipped: ${o.skipped}` : ""}</div>
              <ul className="font-mono">
                {(o.verdicts || []).filter((v) => v.verdict !== "none").map((v, k) => (
                  <li key={k}>camera {v.camera} · {nameOf(names, v.product)} · {v.verdict} · cos {v.cos}</li>
                ))}
              </ul>
              {(o.blind || []).length > 0 && (
                <p className="text-amber-700">
                  Not seeing the basket: {o.blind.map((b) => `camera ${b.camera} ${b.t0}–${b.t1} s (${b.cue === "dark" ? "covered or dark" : b.cue === "lid" ? "lid too high" : b.cue})`).join("; ")}
                </p>
              )}
              {o.weight && (o.weight.products || []).length > 0 && (
                <ul className="text-slate-700">
                  {o.weight.products.map((w, k) => (
                    <li key={k}>weight tray · {nameOf(names, w.product)} · weight {w.weight}{w.units ? ` × ${w.units}` : ""} · camera {w.camera}</li>
                  ))}
                </ul>
              )}
              {(o.details || []).map((d, k) => <details key={k}><summary>camera {d.camera} · {nameOf(names, d.product)} · {d.verdict}</summary><p className="whitespace-pre-wrap break-words text-slate-600">{english(d.detail)}</p></details>)}
            </div>
          ))}
          {(p.conflicts || []).length > 0 && <p className="text-xs text-amber-700">{p.conflicts.length} camera conflict(s), not charged.</p>}
        </div>
      </div>
      <div className="space-y-4">
        {scenes.length > 0 && (
          <div>
            <div className="label">What the camera saw of the basket</div>
            <div className="flex flex-wrap gap-2">
              {scenes.map((c) => (
                <figure key={c.name} className="text-center">
                  {c.url ? <img src={c.url} alt={c.name} className="h-28 rounded bg-black object-contain" /> : <div className="h-28 w-36 rounded bg-black" />}
                  <figcaption className="text-xs text-slate-500">camera {c.camera} · t+{c.t?.toFixed(1)} s</figcaption>
                </figure>
              ))}
            </div>
          </div>
        )}
        <div className="label">Hands and products</div>
        {Object.keys(byCamera).length === 0 ? (
          <p className="text-sm text-slate-500">
            {p.flags.includes("nothing_seen") ? "No product was seen, so there are no hand crops."
              : ev.mode === "off" ? "This cabinet sends no crops."
              : ev.mode === "uncertain" && p.outcome === "charged" ? "Crops are sent only for empty or uncertain purchases on this cabinet."
              : "No crops for this purchase."}
          </p>
        ) : Object.entries(byCamera).map(([cam, cs]) => (
          <div key={cam}>
            <div className="mb-1 text-xs text-slate-500">Camera {cam} · {cs.length} crops</div>
            <CropPlayer crops={cs} names={names} />
          </div>
        ))}
        <p className="text-xs text-slate-500">
          Crops show the inside of the basket only; everything outside it is blacked out on the cabinet, and crops with anything face-like are never sent
          {ev.dropped_face ? ` (${ev.dropped_face} withheld for that)` : ""}. Links expire after 15 minutes; this view is audited.
        </p>
      </div>
    </div>
  );
}

// fixedDeviceId: embedded in a cabinet's page (docs/tasks/35) - this cabinet only.
export function PurchasesPage({ fixedDeviceId = null } = {}) {
  const api = useApi();
  const [filter, setFilter] = useState({ from: "", to: "", basket: "", device_id: fixedDeviceId || "", outcome: "" });
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const [devices, setDevices] = useState([]);
  const [names, setNames] = useState({});
  const [err, setErr] = useState(null);

  const load = () => {
    setErr(null);
    api.listPurchases({ ...filter, from: toEpoch(filter.from), to: toEpoch(filter.to) })
      .then(setItems).catch((e) => setErr(String(e.message || e)));
  };
  useEffect(load, [api]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.listDevices().then((d) => setDevices(d || [])).catch(() => {}); }, [api]);
  useEffect(() => {
    api.listProducts().then((list) => {
      const out = {};
      for (const pr of list || []) {
        const label = pr.product_name || pr.ean_code;   // a product saved without a name still has its EAN
        if (pr.id && label) { out[pr.id] = label; out[pr.id.slice(0, 8)] = label; }
      }
      setNames(out);
    }).catch(() => {});
  }, [api]);
  const deviceName = (id) => devices.find((d) => d.id === id)?.name || (id || "").slice(0, 12);
  const set = (k) => (e) => setFilter((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="space-y-6">
      <div className={fixedDeviceId ? "hidden" : ""}>
        <h1 className="text-2xl font-semibold">Purchases</h1>
        <p className="text-sm text-slate-500">Every purchase, what the cabinet decided and why. Find one by time and basket id to settle a dispute.</p>
      </div>
      <form className="card grid gap-3 md:grid-cols-6" onSubmit={(e) => { e.preventDefault(); load(); }}>
        <label className="text-xs">From<input type="datetime-local" className="input" value={filter.from} onChange={set("from")} /></label>
        <label className="text-xs">To<input type="datetime-local" className="input" value={filter.to} onChange={set("to")} /></label>
        <label className="text-xs">Basket id<input className="input" value={filter.basket} onChange={set("basket")} placeholder="merchant's id" /></label>
        <label className={fixedDeviceId ? "hidden" : "text-xs"}>Cabinet
          <select className="input" value={filter.device_id} onChange={set("device_id")}>
            <option value="">All</option>
            {devices.map((d) => <option key={d.id} value={d.id}>{d.name || d.id}</option>)}
          </select>
        </label>
        <label className="text-xs">Outcome
          <select className="input" value={filter.outcome} onChange={set("outcome")}>
            <option value="">All</option>
            <option value="empty">Empty cart</option>
            <option value="uncertain">Uncertain</option>
            <option value="charged">Charged</option>
          </select>
        </label>
        <div className="flex items-end"><button className="btn btn-primary w-full" type="submit">Search</button></div>
      </form>
      {err && <p className="text-sm text-rose-700">{err}</p>}
      <div className="card overflow-x-auto">
        {items.length === 0 ? <p className="text-sm text-slate-500">No purchases match.</p> : (
          <table className="table">
            <thead><tr><th>Closed</th><th>Basket id</th><th>Cabinet</th><th>Outcome</th><th>Items</th><th>Crops</th><th>Reasons</th></tr></thead>
            <tbody>
              {items.map((p) => (
                [
                  <tr key={p.session_id} className="cursor-pointer hover:bg-slate-50" onClick={() => setOpen(open === p.session_id ? null : p.session_id)}>
                    <td className="text-xs">{fmt(p.closed_at || p.started_at)}</td>
                    <td className="font-mono text-xs">{p.external_session_id || "—"}</td>
                    <td className="text-xs">{deviceName(p.device_id)}</td>
                    <td><span className={`rounded px-2 py-0.5 text-xs ${OUTCOME[p.outcome]?.cls || ""}`}>{OUTCOME[p.outcome]?.label || p.outcome}</span></td>
                    <td>{p.items}</td>
                    <td>{p.crops || ""}</td>
                    <td className="text-xs text-slate-600">{p.flags.map(flagText).join("; ")}</td>
                  </tr>,
                  open === p.session_id && (
                    <tr key={`${p.session_id}-d`}><td colSpan={7}><PurchaseDetail sessionId={p.session_id} names={names} deviceName={deviceName} /></td></tr>
                  ),
                ]
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
